/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {Link} from '../types/entities';

/**
 * Provides an relations of link's returned from the smart search request.
 */
export enum Relations {
  FIRST = 'first',
  NEXT = 'next',
  PREV = 'prev',
  SELF = 'self'
};

/**
 * The `SmartSearchPaging` class provides an API for managing the pagination state of API
 * calls in order to determine the parameters that are to be included with the request based
 * on the page and page size in consideration of the maximum paging limit.
 *
 * ```typescript
 *   // define a paging instance for all smart search calls from a given context ...
 *   const paging = new SmartSearchPaging();
 *   ...
 *
 *   // we pass the paging instance to smart search calls, and the calls manage calling
 *   // both paging.getPaging to determine the current paging parameters, and once the
 *   // response is received, the paging.setLinks(); method is invoked with the latest
 *   // links returned from the response ...
 *   const {devices, totalCount, links} = await loadDevicesBySmartSearch(data, paging);
 *   ...
 *
 *   // /core/src/services/Connect.ts
 *   // pass the paging instance to the API which invokes smart search,
 *   // the API will retrieve the paging parameters via the `getPaging`
 *   // method, and set the response links via the `setLinks` method ...
 *   export const loadDevicesBySmartSearch = async({
 *     pageId = 1,
 *     pageSize = 10,
 *     ...,
 *     paging
 *   }) => {
 *     ...
 *     const pagination = paging.getPaging(pageId, pageSize);
 *     let uri = `/devices/smartsearch?v=${config.ctcVersion}&${pagination}...`;
 *     ...
 *     const response: LoadDevicesResponse = await client.post(...);
 *     const {results, totalCount, _links} = response.data?.response;
 *     paging.setLinks(_links);
 *   }
 *
 * ```
 */
export class SmartSearchPaging {
  /**
   * Defines a reference to the maximum paging limit supported by Smart Search, after
   * which the `searchAfter` parameter and associated values are to be included in the
   * request parameters.
   */
  public static MAX_LIMIT = 10000;

  /**
   * Provides a reference to the `links` returned from the previous smart search request.
   * The links are used to determine the `searchAfter` values if they are required in the
   * request.
   */
  protected links: Link[];

  /**
   * Determine if page + pageSize is less than 10k limit, if so, simply returns an
   * query string containing `pg` and `pgsize` (without a `searchAfter` parameter and
   * value) based on the provided arguments. If page + pageSize is greater than or
   * equal to the 10k limit, the `searchAfter` parameter and value will be included
   * in the returned query string.
   *
   * ```typescript
   *   // define a paging instance for all smart search calls from a given context ...
   *   const paging = new SmartSearchPaging();
   *   ...
   *
   *   // we pass the paging instance to smart search calls, and the calls manage calling
   *   // both paging.getPaging to determine the current paging parameters, and once the
   *   // response is received, the paging.setLinks(); method is invoked with the latest
   *   // links returned from the response ...
   *   const {devices, totalCount, links} = await loadDevicesBySmartSearch(data, paging);
   *
   *   // packages/core/src/services/Connect.ts
   *   // pass the paging instance to the API which invokes smart search
   *   // the API will retrieve the paging parameters via the `getPaging`
   *   // method, and set the response links via the `setLinks` method ...
   *   export const loadDevicesBySmartSearch = async({
   *     pageId = 1,
   *     pageSize = 10,
   *     ...,
   *     paging
   *   }) => {
   *     ...
   *     const pagination = paging.getPaging(pageId, pageSize);
   *     let uri = `/devices/smartsearch?v=${config.ctcVersion}&${pagination}...`;
   *     ...
   *   }
   *
   * ```
   *
   * If page + pageSize is greater than or equal to the 10k limit, returns the
   * searchAfter parameter based on either the `previous` or `next` rel values
   * of the `links` persisted via the previous `setPaging` call.
   *
   * @param   page The current page which is to be retrieved via smart search
   * @param   pageSize The number of the results to be included in the response.
   * @returns An string containing the provided page, pageSize, and an optional
   *          `searchAfter` parameters which are ready for inclusion in the
   *          request query string; e.g.
   *          'pg=123&pgsize=100&searchAfter=1213118113,2C805893'
   */
  public getPaging(page: number, pageSize: number): string {
    if (page * pageSize > SmartSearchPaging.MAX_LIMIT) {
      return this.getSearchParameters();
    }
    return `&pg=${page}&pgsize=${pageSize}`;
  }

  /**
   * Provides an API for setting the links from the previous responses results. The links
   * are then persisted to determine if a `searchAfter` parameter is to be included or not,
   * and if so, the values assigned to `searchAfter` will be based on either the `next`
   * link's `href` `searchAfter` value (if paging to the next page), or the `previous`
   * link's `href` `searchAfter` value (if paging to the previous page).
   *
   * ```typescript
   *   const response: LoadDevicesResponse = await client.post(...);
   *   const {results, totalCount, _links} = response.data?.response;
   *   paging.setLinks(_links);
   *
   * ```
   *
   * @param links The `Link[]` array returned from the smart search response.
   */
  public setLinks(links: Link[]) {
    this.links = links;
  };

  /**
   * Provides a convenience method for retrieving the query string parameters for a
   * `previous` or `next` link's `href`.
   *
   * ```typescript
   *   // adds all parameters within the `next` link's `href`, or `previous` link's
   *   // `href` when the `isNext` argument is set to `false`.
   *   url = `${url}?${paging.getParameters()};
   *
   *   const response: LoadDevicesResponse = await client.post(url);
   *   const {results, totalCount, _links} = response.data?.response;
   *   paging.setLinks(_links);
   *
   * ```
   *
   * @param   relation, when next (default) the query string from the next link's href
   *          will be returned, otherwise, the previous links href querystring will
   *          be returned.
   * @returns The query string from either the previous or next link's `href`.
   */
  public getParameters(relation: Relations): string[] {
    const targetedLink = Array.isArray(this.links)
      ? this.links?.find(({rel}) => rel === relation)?.href
      : this.links?.[relation];
    const url    = decodeURIComponent(targetedLink);
    const query  = url?.split('?')[1];
    const params = query?.split('&');
    return params;
  };

  /**
   * Provides a convenience method for retrieving the `pgId` and `pgSize` and
   * `searchAfter` for `previous` or `next` link's `href`.
   *
   * @param   relation, the `pgId` and `pgSize` and `searchAfter` from `previous`
   *          or `next` link's href will be returned.
   * @returns The `pgId` and `pgSize` and `searchAfter` from `previous` or `next`
   *          link's `href`.
   */
  public getSearchParameters(relation = Relations.NEXT): string {
    let pgId, pgSize, searchAfter;
    const params = this.getParameters(relation);
    params?.forEach(item => {
      const [key, value] = item.split('=');
      if (key === 'pg') {
        pgId = value;
      } else if (key === 'pgSize' || key === 'pgsize') {
        pgSize = value;
      } else if (key === 'searchAfter') {
        searchAfter = value;
      }
    });
    return `&pg=${pgId}&pgsize=${pgSize}&searchAfter=${searchAfter}`;
  };
}

export default SmartSearchPaging;
